import { gettext } from '@eventbrite/i18n';
import { ChoiceListBase, ChoiceListItem, Radio } from '@eventbrite/marmalade';
import React from 'react';
import {
    childClampState,
    ReadMore,
    ReadMoreContent,
    ReadMoreToggle,
} from '../../../../components/ReadMore';
import './FilterSection.scss';
import { useShouldScrollIntoView } from './hooks';
import { FilterItemProps, FilterSectionProps } from './types';

const PREVIEW_CHILD_COUNT = 4;

export const getRadioValues = (filters: object) => {
    const entries = Object.entries(filters);

    return {
        radioValues: entries.map(([key, label]) => ({
            display: label,
            value: key,
        })),
    };
};

export const FilterItem: React.FunctionComponent<FilterItemProps> = ({
    ChoiceComponent = Radio,
    value,
    display,
    children,
}) => {
    return (
        <>
            <ChoiceListItem value={value} label={display}>
                <ChoiceComponent />
            </ChoiceListItem>
            {children}
        </>
    );
};

export const FilterSection = ({
    filters,
    onOptionChange,
    title,
    activeFilterOption,
    isMobile,
    defaultActiveFilterOption = '',
    children,
    shouldScrollIntoView,
}: FilterSectionProps) => {
    const node = useShouldScrollIntoView(!!shouldScrollIntoView);

    const { radioValues } = getRadioValues(filters);

    const handleOptionChange = (value: string) => {
        onOptionChange(value);
    };

    const radioValueElements = radioValues.map((radioValue) =>
        children(radioValue),
    );
    const readMoreA11yLabel = gettext('view-more-%(title)s', {
        title: title.toLowerCase(),
    });

    return (
        <div
            className="filterSection"
            data-testid={`filter-section__${title?.toString()?.toLowerCase()}`}
            ref={node}
        >
            <ReadMore
                ariaLabel={readMoreA11yLabel.toString()}
                initialIsExpanded={shouldScrollIntoView}
                previewChildInitializer={() =>
                    childClampState(
                        PREVIEW_CHILD_COUNT,
                        radioValueElements.length,
                    )
                }
            >
                <ChoiceListBase
                    name={`${title}_${isMobile ? 'mobile' : 'desktop'}`}
                    size={'small'}
                    selected={activeFilterOption || defaultActiveFilterOption}
                    onChange={(val: string) => handleOptionChange(val)}
                    title={title}
                    titleHidden={false}
                >
                    <ul
                        className="filter-choice-items"
                        id={readMoreA11yLabel.toString()}
                    >
                        <ReadMoreContent
                            previewChildCount={PREVIEW_CHILD_COUNT}
                            previewContentType="children"
                        >
                            {radioValueElements}
                        </ReadMoreContent>
                    </ul>
                </ChoiceListBase>
                <ReadMoreToggle toggleClass="filter-toggle" />
            </ReadMore>
        </div>
    );
};

export default FilterSection;
